import React, { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Invoice } from '@/types/invoice'
import { formatCurrency, getDaysOverdue } from '@/lib/utils'
import { TrendingUp, TrendingDown, Euro, Clock, AlertTriangle, CheckCircle } from 'lucide-react'

interface AnalyticsDashboardProps {
  invoices: Invoice[]
}

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  trend?: 'up' | 'down' | 'neutral'
  trendValue?: string
  className?: string
}

function StatCard({ title, value, icon, trend, trendValue, className = '' }: StatCardProps) {
  const getTrendColor = () => {
    switch (trend) {
      case 'up': return 'text-green-600'
      case 'down': return 'text-red-600'
      default: return 'text-muted-foreground'
    }
  }

  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4" />
      case 'down': return <TrendingDown className="w-4 h-4" />
      default: return null
    }
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {trend && trendValue && (
          <div className={`flex items-center text-xs ${getTrendColor()}`}>
            {getTrendIcon()}
            <span className="ml-1">{trendValue}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function AnalyticsDashboard({ invoices }: AnalyticsDashboardProps) {
  const stats = useMemo(() => {
    // Detect the most common currency from invoices
    const currencyCounts = invoices.reduce((acc, inv) => {
      const currency = inv.currency || 'EUR'
      acc[currency] = (acc[currency] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    
    const primaryCurrency = Object.keys(currencyCounts).length > 0 
      ? Object.entries(currencyCounts).sort(([,a], [,b]) => b - a)[0][0]
      : 'EUR'

    const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0)
    const overdueInvoices = invoices.filter(inv => getDaysOverdue(inv.dueDate) > 0)
    const overdueAmount = overdueInvoices.reduce((sum, inv) => sum + inv.amount, 0)
    const averageDaysOverdue = overdueInvoices.length > 0 
      ? overdueInvoices.reduce((sum, inv) => sum + getDaysOverdue(inv.dueDate), 0) / overdueInvoices.length
      : 0
    
    const criticalInvoices = overdueInvoices.filter(inv => getDaysOverdue(inv.dueDate) > 30)
    const criticalAmount = criticalInvoices.reduce((sum, inv) => sum + inv.amount, 0)
    
    const recentInvoices = invoices.filter(inv => {
      const dueDate = new Date(inv.dueDate)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      return dueDate >= thirtyDaysAgo
    })
    
    const recentAmount = recentInvoices.reduce((sum, inv) => sum + inv.amount, 0)

    return {
      totalAmount,
      overdueCount: overdueInvoices.length,
      overdueAmount,
      averageDaysOverdue: Math.round(averageDaysOverdue),
      criticalCount: criticalInvoices.length,
      criticalAmount,
      recentCount: recentInvoices.length,
      recentAmount,
      totalCount: invoices.length,
      primaryCurrency
    }
  }, [invoices])

  const getStatusColor = (daysOverdue: number) => {
    if (daysOverdue === 0) return 'text-green-600'
    if (daysOverdue <= 7) return 'text-yellow-600'
    if (daysOverdue <= 30) return 'text-orange-600'
    return 'text-red-600'
  }

  const getStatusIcon = (daysOverdue: number) => {
    if (daysOverdue === 0) return <CheckCircle className="w-4 h-4" />
    if (daysOverdue <= 7) return <Clock className="w-4 h-4" />
    return <AlertTriangle className="w-4 h-4" />
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Outstanding"
          value={formatCurrency(stats.totalAmount, stats.primaryCurrency)}
          icon={<Euro className="w-4 h-4" />}
          className="border-l-4 border-l-blue-500"
        />
        
        <StatCard
          title="Overdue Invoices"
          value={stats.overdueCount}
          icon={<AlertTriangle className="w-4 h-4" />}
          trend={stats.overdueCount > 0 ? 'up' : 'neutral'}
          trendValue={`${stats.overdueCount} invoices`}
          className="border-l-4 border-l-red-500"
        />
        
        <StatCard
          title="Overdue Amount"
          value={formatCurrency(stats.overdueAmount, stats.primaryCurrency)}
          icon={<Euro className="w-4 h-4" />}
          trend={stats.overdueAmount > 0 ? 'up' : 'neutral'}
          trendValue={`${((stats.overdueAmount / stats.totalAmount) * 100).toFixed(1)}% of total`}
          className="border-l-4 border-l-orange-500"
        />
        
        <StatCard
          title="Avg Days Overdue"
          value={stats.averageDaysOverdue}
          icon={<Clock className="w-4 h-4" />}
          trend={stats.averageDaysOverdue > 7 ? 'up' : 'down'}
          trendValue="days"
          className="border-l-4 border-l-yellow-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
                             Critical Invoices ({'>'}30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="font-semibold">{stats.criticalCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Amount:</span>
                <span className="font-semibold text-red-600">
                  {formatCurrency(stats.criticalAmount, stats.primaryCurrency)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              Recent Invoices (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="font-semibold">{stats.recentCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Amount:</span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(stats.recentAmount, stats.primaryCurrency)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>


    </div>
  )
} 